import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Logo from '../components/Logo';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, CheckCircle2, AlertCircle, Eye, EyeOff } from "lucide-react";

const ResetPasswordPage: React.FC = () => {
  const [formData, setFormData] = useState({
    businessId: '',
    token: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const { businessId, token } = useParams();
  
  useEffect(() => {
    if (businessId && token) {
      setFormData(prev => ({ ...prev, businessId, token }));
    }
  }, [businessId, token]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    
    if (!formData.businessId || !formData.token || !formData.newPassword) {
      setError('Bitte füllen Sie alle Felder aus');
      return;
    }
    
    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwörter stimmen nicht überein');
      return;
    }

    try {
      setLoading(true);
      const result = await resetPassword(formData.businessId, formData.token, formData.newPassword);
      
      if (result) {
        setSuccess(true);
        setTimeout(() => navigate('/login'), 3000);
      } else {
        setError('Passwort konnte nicht zurückgesetzt werden. Der Link ist möglicherweise abgelaufen.');
      }
    } catch (err) {
      setError('Ein Fehler ist aufgetreten');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <div className="flex-1 flex flex-col justify-center items-center p-4">
        <div className="w-full max-w-md space-y-8">
          <div className="flex justify-center mb-4">
            <Logo />
          </div>

          <Card className="border-border shadow-sm">
            <CardContent className="p-6 md:p-8">
              <div className="text-center mb-6">
                <h1 className="text-2xl font-semibold text-foreground mb-2">Neues Passwort erstellen</h1>
                <p className="text-sm text-muted-foreground">
                  Bitte geben Sie Ihr neues Passwort ein.
                </p>
              </div>

              {error && (
                <Alert variant="destructive" className="mb-6">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {success ? (
                <Alert className="bg-success/10 border-success/25 text-success-strong">
                  <CheckCircle2 className="h-4 w-4 text-success-strong" />
                  <AlertDescription>
                    Passwort erfolgreich geändert! Sie werden gleich zur Anmeldung weitergeleitet.
                  </AlertDescription>
                </Alert>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="newPassword">Neues Passwort</Label>
                    <div className="relative">
                      <Input
                        id="newPassword"
                        name="newPassword"
                        type={showPassword ? 'text' : 'password'}
                        value={formData.newPassword}
                        onChange={handleChange}
                        disabled={loading}
                        required
                        className="pr-10"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-10 w-10 text-muted-foreground hover:text-foreground"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        name="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        disabled={loading}
                        required
                        className="pr-10"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-10 w-10 text-muted-foreground hover:text-foreground"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-[#1967D2] hover:bg-[#1756B6]" 
                    disabled={loading}
                  >
                    {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Passwort speichern'}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>

          <div className="text-center">
            <Button asChild variant="link" className="text-muted-foreground hover:text-foreground">
              <RouterLink to="/login">Zurück zur Anmeldung</RouterLink>
            </Button>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <div className="py-4 flex justify-center items-center opacity-60">
        <span className="text-xs text-muted-foreground mr-2">powered by</span>
        <img src="/images/EcomTask_logo.svg" alt="EcomTask" className="h-5 opacity-60 grayscale" />
      </div>
    </div>
  );
};

export default ResetPasswordPage;
